import { AuthenticatedUser, AuthStore, getCurrentAuthMethod, TokenResponse, UserInfo } from 'auth';
import {
  allowActingAsAllAtom,
  isProxyAtom,
  proxyIntermediaryListAtom
} from 'auth/store/auth-store';
import {
  Configuration,
  ResultOfAccessResponseViewModel,
  SecurityAccessFeaturesEnum,
  SecurityApi
} from 'bff';
import { getActingAsIntermediaryFromStorage } from 'providers/managers/user-context-manager/acting-as-user-storage';
import { Env } from 'types';
import { UserWithFeatures } from 'types/auth/user-context.type';
import { getAuthHeader } from './get-auth-header';
import { jotaiStore } from 'stores/jotai.store';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { AuthNavigate } from 'auth/types/auth-navigate.type';
import { routes } from 'routes/route-paths';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';

type CreateGetAccessProps = {
  getSignedInUser: () => AuthenticatedUser | undefined;
  setSignedInUser: (user: UserInfo) => void;
  setCurrentAuthenticatedUserWithFeatures: (actingAsUser: UserWithFeatures | undefined) => void;
  env: Env;
  navigate: AuthNavigate;
};

const handleOnSuccess =
  (createGetAccessProps: CreateGetAccessProps) => (result: ResultOfAccessResponseViewModel) => {
    const { setSignedInUser, setCurrentAuthenticatedUserWithFeatures, navigate } =
      createGetAccessProps;

    AuthStore.setUserInfo({
      display_name: `${result.data?.userInformation.firstName} ${result.data?.userInformation.surname}`,
      email: result.data?.userInformation.emailAddress ?? '',
      family_name: result.data?.userInformation.surname ?? '',
      given_name: result.data?.userInformation.firstName ?? '',
      sub: result.data?.userInformation.sanpayCode ?? '',
      groups: '',
      guid: '',
      name: result.data?.userInformation.firstName ?? '',
      phone_number: '',
      preferred_username: '',
      nickname: '',
      login: result.data?.userInformation.sanpayCode ?? ''
    });

    setSignedInUser({
      display_name: `${result.data?.userInformation.firstName} ${result.data?.userInformation.surname}`,
      email: result.data?.userInformation.emailAddress ?? '',
      family_name: result.data?.userInformation.surname ?? '',
      given_name: result.data?.userInformation.firstName ?? '',
      sub: result.data?.userInformation.sanpayCode ?? '',
      groups: '',
      guid: '',
      name: result.data?.userInformation.firstName ?? '',
      phone_number: '',
      preferred_username: '',
      nickname: '',
      login: result.data?.userInformation.sanpayCode ?? ''
    });

    const pathName: string = location.pathname;

    const isCurrentlyOnIntermediarySelectScreen = pathName.includes(routes.selectIntermediary);
    const hasActingAsInStorage = !!getActingAsIntermediaryFromStorage();
    const allowActingAs = result.data?.features.includes(SecurityAccessFeaturesEnum.AllowActingAs);
    const allowActingAsAll = result.data?.features.includes(
      SecurityAccessFeaturesEnum.AllowActingAsAll
    );

    setCurrentAuthenticatedUserWithFeatures({
      features: result.data?.features ?? [],
      firstName: result.data?.userInformation.firstName ?? '',
      lastName: result.data?.userInformation.surname ?? '',
      intermediaryCode: result.data?.userInformation.sanpayCode ?? ''
    });

    const noFeatures = result.data?.features.length === 0;
    const shouldNavigateSelectIntermediary =
      allowActingAs && !isCurrentlyOnIntermediarySelectScreen && !hasActingAsInStorage;

    if (noFeatures) {
      navigate(routes.comingSoon);
    }

    if (shouldNavigateSelectIntermediary) {
      navigate(routes.selectIntermediary);
    }

    jotaiStore.set(allowActingAsAllAtom, allowActingAsAll ?? false);
    jotaiStore.set(isProxyAtom, allowActingAs || allowActingAsAll || false);
  };

export const getAccess =
  (createGetAccessProps: CreateGetAccessProps) =>
  async (authToken?: TokenResponse | undefined): Promise<boolean> => {
    const { env } = createGetAccessProps;
    const { isUsingWebseal } = getCurrentAuthMethod(env);

    const config = new Configuration({
      basePath: replaceWithDynamicHost(env.apiUrl, env.apiHostMappings),
      apiKey: getAuthHeader(
        replaceWithDynamicHost(env.apiUrl, env.apiHostMappings),
        !isUsingWebseal,
        env.auth.gateway.jwtProvider,
        authToken
      ),
      accessToken: authToken?.access_token
    });

    const securityApi = new SecurityApi(config);

    try {
      const response = await securityApi.securityGetAccess({ intermediaryCode: undefined });

      jotaiStore.set(proxyIntermediaryListAtom, response.data);
      handleOnSuccess(createGetAccessProps)(response);
      return true;
    } catch (error) {
      logger.error({ message: `${error}` });
      return false;
    }
  };
